import React from 'react';
import { 
  List, 
  Typography, 
  Paper, 
  Divider, 
  Box, 
  Alert,
  CircularProgress
} from '@mui/material';
import TodoItem from './TodoItem';
import { TodoWithState } from '../../types/todos';

interface TodoListProps {
  todos: TodoWithState[];
  loading: boolean;
  error: string | null;
  onToggleTodo: (id: string, isComplete: boolean) => Promise<void>;
  onDeleteTodo: (id: string) => Promise<void>;
}

const TodoList: React.FC<TodoListProps> = ({ 
  todos, 
  loading, 
  error, 
  onToggleTodo, 
  onDeleteTodo 
}) => {
  if (loading && todos.length === 0) {
    return (
      <Box display="flex" justifyContent="center" my={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (todos.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="textSecondary">
          No todos yet. Add one above!
        </Typography>
      </Paper>
    );
  }

  // Separate incomplete and complete todos
  const incompleteTodos = todos.filter(todo => !todo.is_complete);
  const completeTodos = todos.filter(todo => todo.is_complete);

  return (
    <Paper sx={{ width: '100%', mt: 1 }}>
      <List>
        {incompleteTodos.map((todo) => (
          <TodoItem
            key={todo.id}
            todo={todo}
            onToggle={onToggleTodo}
            onDelete={onDeleteTodo}
          />
        ))}

        {incompleteTodos.length > 0 && completeTodos.length > 0 && (
          <>
            <Divider component="li" />
            <Box sx={{ py: 1, px: 2 }}>
              <Typography color="textSecondary" variant="body2">
                Completed ({completeTodos.length})
              </Typography>
            </Box>
            <Divider component="li" />
          </>
        )}

        {completeTodos.map((todo) => (
          <TodoItem
            key={todo.id}
            todo={todo}
            onToggle={onToggleTodo}
            onDelete={onDeleteTodo}
          />
        ))}
      </List>
    </Paper>
  );
};

export default TodoList;